import React,{Component} from "react";
import ReactDOM from 'react-dom/client';
import PropTypes from "prop-types";
import "./HamburgerArea.css";
import Glob from "../../libs/Glob.mjs";

const prefix = 'HamburgerArea';

const DefaultHamburgerIcon = class extends Component {
	render() {
		return (
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" stroke="none" style={this.props.style||{}}>
				<path d="M27,23v2a1,1,0,0,1-1,1H6a1,1,0,0,1-1-1V23a1,1,0,0,1,1-1H26A1,1,0,0,1,27,23ZM26,6H6A1,1,0,0,0,5,7V9a1,1,0,0,0,1,1H26a1,1,0,0,0,1-1V7A1,1,0,0,0,26,6Zm0,8H6a1,1,0,0,0-1,1v2a1,1,0,0,0,1,1H26a1,1,0,0,0,1-1V15A1,1,0,0,0,26,14Z"/>
			</svg>
		);
	}
}

const globalCloser = () => {
	Glob.trigger(prefix+'Opens', 'no-menu-id');
	setTimeout( ()=>{
		window.document.querySelectorAll(`body > .${prefix}HiWrap`).forEach(n=>{
			const id = n.getAttribute('id');
			const w = window.document.getElementById( id.replace(/-hiwrap$/,'-wrap'));
			if ( ! w ) n.remove();
		});
	});
};

const opener = (id, container, content ) => {
	const doc = window.document;
	const menu = doc.getElementById(id);
	const isEnd = menu.classList.contains('end');
	const box = menu.querySelector(`.${prefix}Trigger`).getBoundingClientRect();
	const d = doc.createElement('div');
	d.setAttribute('id', id+'-hiwrap');
	d.addEventListener('mouseleave', ()=>{ Glob.trigger(prefix+'Opens', 'no-menu-id'); });
	d.className = prefix+'HiWrap';
	d.style.top = String(box.top + window.scrollY)+'px';
	if ( isEnd ) {
		d.style.right = String(window.document.body.clientWidth - box.right  - window.scrollX)+'px';
	} else {
		d.style.left = String(box.left  + window.scrollX)+'px';
	}
	d.appendChild(menu);
	doc.body.appendChild(d);
	container.render(
		<React.StrictMode>
			{ content }
		</React.StrictMode>
	);
	menu.classList.remove('collapsed');
	menu.classList.add('expanded');
};

const closer = (id) => {
	const doc = window.document;
	const hiwrap = doc.getElementById(id+'-hiwrap');
	if ( ! hiwrap ) return;
	const wrap = doc.getElementById(id+'-wrap');
	const menu = doc.getElementById(id);
	if ( menu ) {
		menu.classList.add('collapsed');
		menu.classList.remove('expanded');
		if ( wrap ) wrap.appendChild(menu);
	}
	hiwrap.remove();
}

/**
 * @private
 * @typedef HamburgerAreaProps
 * @type {Object}
 * @property {"start"|"center"|"end"} [align] Alignments, defaults to start
 * @property {string|Component|JSX.Element} [icon] Defaults to SVG hamburger icon
 */

/**
 * @desc Builds the Hamburger Menu and its area
 * @abstract An hamburger area is a self-collapsable and expandable area
 * marked by a text or SVG icon.
 * @abstract Children node of this object will be shown/hidden depending on
 * expanded/collapsed state of the hamburger
 * @abstract NOTE: By default starts collapsed.
 * @version 1.1
 * @author [Marco Balestra](https://altersoftware.it)
 * @type {Component}
 * @property  {"start"|"center"|"end"} [align] Alignments, defaults to start
 * @property {string|Component|JSX.Element} [icon] Defaults to SVG hamburger icon
 * @property {boolean} [expanded] Initially expanded
 * @property {any} [children] Children nodes
 */
const HamburgerArea = class extends Component {
	static propTypes = {
		align: PropTypes.oneOf(["start"|"center"|"end"]),
		icon: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.instanceOf(Component)]),
		/** Initially expanded */
		expanded: PropTypes.bool,
		children: PropTypes.oneOfType([
			PropTypes.arrayOf(PropTypes.node),
			PropTypes.node
		]).isRequired
	}
	/** @param {HamburgerAreaProps} props */
	constructor(props) {
		super(props);
		this.state = {
			align: props.align || 'start',
			icon: props.icon ?? <DefaultHamburgerIcon />,
			expanded: !! props.expanded
		};
		this.toggle = this.toggle.bind(this);
		this.uid = Glob.generateUid(prefix);
		const doc = window.document;
		if ( ! doc.body.classList.contains(prefix+'Aware') ) {
			doc.body.classList.add(prefix+'Aware');
			doc.addEventListener('click', globalCloser );
			Glob.register('windowResized', ()=>{
				Glob.trigger(prefix+'Opens', 'no-menu-id');
			});
		}
	}
	componentDidMount() {
		Glob.register(prefix+"Opens", id=>{
			if ( id !== this.uid ) this.toggle(false);
		}, this.uid);
	}
	componentWillUnmount() {
		Glob.deregister(this.uid);
	}
	toggle(mode) {
		if ( typeof mode === "undefined" ) {
			mode = ! this.state.expanded;
		} else if ( mode === !! this.state.expanded ) {
			return;
		}
		const doc = window.document;
		const container = this.state.container ||
			ReactDOM.createRoot(doc.querySelector(`#${this.uid} .${prefix}Content`));
		if ( mode ) {
			opener( this.uid, container, this.props.children );
		} else {
			closer( this.uid, container );
		}
		this.setState( { expanded: mode, container:container } );
		Glob.trigger(prefix+(mode ? 'Opens' : 'Closes'), this.uid);
	}
	render() {
		return(
			<div
				id={this.uid + "-wrap"}
				className={prefix+"Wrap"}
			>
				<div
					className={`${prefix} collapsed ${this.state.align}${this.props.className ? ' '+this.props.className : ''}`}
					id={this.uid}
				>
					<div
						className={prefix+"Trigger"}
						onClick={(evt) => {
							evt.stopPropagation();
							this.toggle();
						}}
					>
						{ this.state.icon }
					</div>
					<div className={prefix+"Content"} />
				</div>
			</div>
		)
	}
}

export default HamburgerArea;